import React, { useState, useEffect, useRef, useCallback } from 'react';
import wirelessApi from '../../services/wirelessApi';

/**
 * The unattended catalogue run, and where it has got to.
 *
 * A campaign takes tens of minutes — one adapter means the tests run serially
 * and W-11 alone waits half an hour for a natural association — so the useful
 * thing to show is not a percentage but *which phase*, and what each phase is
 * for. A bar creeping across the screen for forty minutes tells an operator
 * nothing they can act on; "capturing handshakes, 2 of 4 networks" does.
 *
 * The phases are also the argument for the ordering, so they are labelled with
 * it: deauthentication is visibly last because it destroys the captures that
 * precede it.
 */

const PHASES = [
  { id: 'survey', label: 'Survey', hint: 'Listening — building the estate' },
  { id: 'scan', label: 'Active scan', hint: '802.11w, AKM, WPS, hidden SSIDs' },
  { id: 'evaluate', label: 'Evaluate', hint: 'Deterministic verdicts' },
  { id: 'capture', label: 'Capture', hint: 'PMKID and handshakes — passive' },
  { id: 'disruptive', label: 'Deauth', hint: 'Last: it destroys captures' },
  { id: 'reevaluate', label: 'Re-evaluate', hint: 'Did clients actually drop?' },
];

const OPT_INS = [
  { id: 'rogue_ap', label: 'Evil twin (W-30)',
    warn: 'Impersonates a corporate SSID; real staff devices associate to it.' },
  { id: 'post_assoc', label: 'Post-association (W-40–43)',
    warn: 'Needs a second radio, and a cable to the AP uplink for W-42.' },
  { id: 'offline_crack', label: 'Offline recovery (W-13)',
    warn: 'Runs for hours or days on separate hardware.' },
];

// Long enough for a channel-hopping sweep to visit every channel repeatedly.
const SURVEY_MINUTES = [5, 10, 20, 30];

function CampaignPanel({ engagementId, onChanged }) {
  const [campaign, setCampaign] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [surveyMinutes, setSurveyMinutes] = useState(10);
  const [optIns, setOptIns] = useState({});
  const timer = useRef(null);

  const running = campaign?.status === 'running' && !campaign?.stale;

  const load = useCallback(async () => {
    try {
      const data = await wirelessApi.getCampaign(engagementId);
      setCampaign(data);
      return data;
    } catch {
      return null;
    }
  }, [engagementId]);

  useEffect(() => { load(); }, [load]);

  // Poll only while something is happening. A finished campaign does not
  // change, and a page left open overnight should not talk to the API all
  // night to discover that.
  useEffect(() => {
    if (!running) {
      if (timer.current) clearInterval(timer.current);
      return undefined;
    }
    timer.current = setInterval(async () => {
      const data = await load();
      if (data && data.status !== 'running' && onChanged) onChanged();
    }, 10000);
    return () => clearInterval(timer.current);
  }, [running, load, onChanged]);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await wirelessApi.startCampaign(engagementId, {
        survey_seconds: surveyMinutes * 60,
        ...optIns,
      });
      setCampaign(data);
      setOpen(false);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      setCampaign(await wirelessApi.stopCampaign(campaign.id));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const activeIndex = PHASES.findIndex((p) => p.id === campaign?.phase);
  const counts = campaign?.counts || {};

  return (
    <div className="mb-6 rounded-lg bg-slate-900/50 border border-slate-800 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-200">Automated run</h2>
          <p className="text-[11px] text-slate-500 mt-0.5 max-w-xl">
            Survey, active scan, the eight deterministic evaluators, then captures
            against the busiest authorised access point of each network.
            Deauthentication runs last, because it destroys the captures before it.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {running ? (
            <button
              onClick={stop}
              disabled={busy}
              className="px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 text-[11px] hover:bg-slate-800 disabled:opacity-50"
              title="The test in flight finishes on the agent; the campaign stops advancing."
            >
              Stop
            </button>
          ) : (
            <button
              onClick={() => setOpen((v) => !v)}
              disabled={busy}
              className="px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-medium disabled:opacity-50 transition-colors"
            >
              {campaign ? 'Run again' : 'Run catalogue'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 p-2 rounded bg-red-950/30 border border-red-500/40 text-[11px] text-slate-300">
          {error}
        </div>
      )}

      {open && !running && (
        <div className="mt-4 p-3 rounded-lg bg-slate-900/70 border border-slate-800">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[11px] text-slate-400">Survey for</span>
            {SURVEY_MINUTES.map((m) => (
              <button
                key={m}
                onClick={() => setSurveyMinutes(m)}
                className={`px-2 py-1 rounded text-[10px] border transition-colors ${
                  surveyMinutes === m
                    ? 'bg-slate-800 text-slate-200 border-slate-600'
                    : 'text-slate-500 border-slate-800 hover:border-slate-600'}`}
              >
                {m} min
              </button>
            ))}
          </div>

          {/* Excluded by default, each for its own reason, and the reason is
              shown rather than left to be remembered. */}
          <p className="text-[11px] text-slate-500 mb-2">
            These are not run automatically. Selecting one schedules it as an open
            run for an analyst to perform:
          </p>
          <div className="space-y-1.5">
            {OPT_INS.map((o) => (
              <label key={o.id} className="flex items-start gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={Boolean(optIns[o.id])}
                  onChange={(e) => setOptIns((p) => ({ ...p, [o.id]: e.target.checked }))}
                  className="mt-0.5 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-0"
                />
                <span className="text-[11px]">
                  <span className="text-slate-300 group-hover:text-slate-200">{o.label}</span>
                  <span className="text-slate-600"> — {o.warn}</span>
                </span>
              </label>
            ))}
          </div>

          <button
            onClick={start}
            disabled={busy}
            className="mt-3 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-medium disabled:opacity-50"
          >
            {busy ? 'Starting…' : 'Start'}
          </button>
        </div>
      )}

      {campaign && (
        <div className="mt-4">
          <div className="flex items-stretch">
            {PHASES.map((phase, i) => {
              const done = campaign.status === 'completed'
                || (activeIndex > -1 && i < activeIndex);
              const active = running && i === activeIndex;
              return (
                <div key={phase.id} className="flex-1 min-w-0">
                  <div className="flex items-center">
                    <span
                      className={`w-2.5 h-2.5 rounded-full shrink-0 transition-colors ${
                        active ? 'bg-indigo-400 animate-pulse'
                        : done ? 'bg-emerald-500'
                        : 'bg-slate-700'}`}
                    />
                    {i < PHASES.length - 1 && (
                      <span className={`h-px flex-1 ${done ? 'bg-emerald-600/60' : 'bg-slate-800'}`} />
                    )}
                  </div>
                  <div className="pr-2 pt-1.5">
                    <div className={`text-[10px] font-medium truncate ${
                      active ? 'text-indigo-300' : done ? 'text-slate-400' : 'text-slate-600'}`}>
                      {phase.label}
                    </div>
                    {active && (
                      <div className="text-[10px] text-slate-500 mt-0.5">{phase.hint}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px]">
            <span className={
              campaign.status === 'completed' ? 'text-emerald-400'
              : campaign.status === 'failed' ? 'text-red-400'
              : campaign.stale ? 'text-amber-400'
              : campaign.status === 'cancelled' ? 'text-slate-400' : 'text-indigo-300'}>
              {campaign.stale ? 'stopped reporting' : campaign.status}
            </span>
            {counts.targets != null && <span className="text-slate-500">{counts.targets} networks</span>}
            {counts.evaluated != null && <span className="text-slate-500">{counts.evaluated} evaluated</span>}
            {Array.isArray(counts.selected_targets) && counts.selected_targets.length > 0 && (
              <span className="text-slate-500 truncate max-w-md" title={counts.selected_targets.join(', ')}>
                tested: {counts.selected_targets.join(', ')}
              </span>
            )}
            {counts.scheduled_manual > 0 && (
              <span className="text-amber-400">{counts.scheduled_manual} scheduled for an analyst</span>
            )}
          </div>

          {/* A campaign whose worker died stops beating. Saying so beats a
              spinner that never resolves. */}
          {campaign.stale && (
            <p className="mt-2 text-[11px] text-amber-400/80">
              No progress reported for over 15 minutes — the worker running this
              campaign is gone. Its completed phases stand; start a new run to continue.
            </p>
          )}
          {campaign.error && (
            <p className="mt-2 text-[11px] text-red-400/90">{campaign.error}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default CampaignPanel;
